import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Loader2, ShieldCheck } from "lucide-react";

/** Password-reset landing page. Reads ``?token=…`` and POSTs it
 * with the new password to /api/auth/reset-password. */
export default function ResetPassword() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const t = {
    title: isAr ? "كلمة مرور جديدة" : "Choose a new password",
    lead: isAr
      ? "أدخل كلمة المرور الجديدة. يجب أن تتكون من 8 أحرف على الأقل."
      : "Enter your new password. At least 8 characters.",
    pwd: isAr ? "كلمة المرور الجديدة" : "New password",
    confirm: isAr ? "تأكيد كلمة المرور" : "Confirm password",
    submit: isAr ? "حفظ كلمة المرور" : "Save new password",
    okTitle: isAr ? "تم بنجاح" : "All done",
    mismatch: isAr ? "كلمتا المرور غير متطابقتين." : "Passwords don't match.",
    tooShort: isAr ? "8 أحرف على الأقل." : "At least 8 characters.",
    badToken: isAr ? "الرابط غير صالح." : "Invalid link.",
    err: isAr ? "حدث خطأ، حاول مرة أخرى." : "Something went wrong.",
    backLogin: isAr ? "تسجيل الدخول" : "Back to sign-in",
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-32 text-center" data-testid="reset-no-token">
        <p className="text-slate-600">{t.badToken}</p>
        <Link to="/forgot-password" className="inline-block mt-4 underline underline-offset-4">
          {t.title}
        </Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error(t.tooShort);
      return;
    }
    if (pwd !== confirm) {
      toast.error(t.mismatch);
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success(t.okTitle);
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 py-24">
      <ShieldCheck className="w-10 h-10 text-slate-400" />
      <h1 className="font-display text-4xl mt-6 tracking-tight">{t.title}</h1>
      <p className="mt-3 text-slate-600">{t.lead}</p>

      <form onSubmit={onSubmit} className="mt-12 grid gap-5" data-testid="reset-password-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.pwd}</Label>
          <Input
            type="password"
            required
            minLength={8}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            data-testid="reset-password-pwd"
            className="mt-2 rounded-sm h-11"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.confirm}</Label>
          <Input
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-password-confirm"
            className="mt-2 rounded-sm h-11"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !pwd || !confirm}
          data-testid="reset-password-submit"
          className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.submit}
        </Button>
      </form>
    </div>
  );
}
